import { useTranslation } from 'react-i18next';

import { RANK_FRAME_COLORS, RANKS } from '@/config/ranks';
import { cn } from '@/lib/cn';
import { rankByLevel } from '@/lib/ranks';

/**
 * Escudo del rango, que acompana al alias en todas las pantallas (9.1).
 *
 * La decoracion CRECE con el nivel: un Recluta lleva el hoplon pelado y el Rey
 * de Esparta lo lleva todo —rayos, corona con gemas, laureles atados, lanzas
 * cruzadas y la brasa animada—. Se ve de un vistazo lo lejos que esta uno del
 * otro, que es justo lo que tiene que hacer un sistema de progresion.
 *
 *   1 Recluta        hoplon liso
 *   2 Efebo          + remaches en el canto
 *   3 Hoplita        + lanzas cruzadas detras
 *   4 Enomotarca     + ramas de laurel
 *   5 Locago         + penacho sobre el escudo
 *   6 Polemarca      + corona y lazo que cierra el laurel
 *   7 Rey de Esparta + rayos, gemas, doble aro y brasa animada
 *
 * A tamano pequeno —una fila de ranking— toda esa filigrana seria una mancha,
 * asi que por debajo de 40 px se dibuja solo el escudo con su cifra.
 */

interface RankShieldProps {
  level: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** Para escalonar animaciones desde fuera (p. ej. el desfase del flotado). */
  style?: React.CSSProperties;
  /** Muestra el nombre del rango junto al escudo. */
  withLabel?: boolean;
}

const SIZES: Record<NonNullable<RankShieldProps['size']>, string> = {
  xs: 'h-5 w-5 text-[9px]',
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-11 w-11 text-[13px]',
  lg: 'h-16 w-16 text-lg',
  xl: 'h-24 w-24 text-2xl',
};

/** Por debajo de md no se dibujan ornamentos: no se distinguirian. */
const DETAILED: Record<NonNullable<RankShieldProps['size']>, boolean> = {
  xs: false,
  sm: false,
  md: true,
  lg: true,
  xl: true,
};

export function RankShield({
  level,
  size = 'sm',
  className,
  style,
  withLabel,
}: RankShieldProps) {
  const { t } = useTranslation();
  const rank = rankByLevel(level);
  const colors = RANK_FRAME_COLORS[rank.frame];
  const name = t(`ranks.${rank.code}`);
  const detailed = DETAILED[size];

  const label = t('a11y.rankShield', { rank: name });

  // Dos mundos, dos piezas. En la portada, el escudo espartano con su
  // filigrana; en la app y el panel, una placa del parque con la cifra y una
  // regleta de muescas. Se pintan las dos y el CSS del mundo oculta la ajena:
  // `display: none` tambien la saca del arbol de accesibilidad, asi que el
  // lector de pantalla solo anuncia una.
  const shield = (
    <>
      <span
        className={cn(
          'only-espartano relative inline-flex shrink-0 items-center justify-center font-display font-semibold',
          SIZES[size],
          className,
        )}
        style={style}
        role="img"
        aria-label={label}
      >
        {detailed ? (
          <OrnateShield level={rank.level} stroke={colors.stroke} glow={colors.glow} />
        ) : (
          <PlainShield stroke={colors.stroke} />
        )}

        <span
          className={cn('tabular relative', detailed && 'translate-y-[6%]')}
          style={{ color: colors.stroke }}
        >
          {rank.level}
        </span>
      </span>

      <RankPlate level={rank.level} size={size} label={label} className={className} style={style} />
    </>
  );

  if (!withLabel) return shield;

  return (
    <span className="inline-flex items-center gap-2">
      {shield}
      <span className="font-display text-sm uppercase">{name}</span>
    </span>
  );
}

const PLATE_SIZES: Record<NonNullable<RankShieldProps['size']>, string> = {
  xs: 'h-5 min-w-[1.25rem] px-1 text-[11px]',
  sm: 'h-7 min-w-[1.75rem] px-1.5 text-sm',
  md: 'h-11 w-11 text-xl',
  lg: 'h-16 w-16 text-3xl',
  xl: 'h-24 w-24 text-5xl',
};

const NOTCH_SIZES: Record<NonNullable<RankShieldProps['size']>, string> = {
  xs: '',
  sm: '',
  md: 'h-[3px] w-[3px]',
  lg: 'h-1 w-1',
  xl: 'h-1.5 w-1.5',
};

/**
 * Placa de rango del parque: la cifra del nivel y, a partir de md, una
 * regleta con una muesca por rango, rellenas hasta el propio. Donde el escudo
 * espartano crecia en adornos, la placa crece en muescas: la misma lectura de
 * "cuanto falta" con el material de una barra de parque. El rango maximo va
 * invertido, como la placa activa de la navegacion.
 */
function RankPlate({
  level,
  size,
  label,
  className,
  style,
}: {
  level: number;
  size: NonNullable<RankShieldProps['size']>;
  label: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const top = level >= RANKS.length;
  const notches = DETAILED[size];

  return (
    <span
      className={cn(
        'only-parque relative inline-flex shrink-0 flex-col items-center justify-center rounded-[2px] font-display font-bold leading-none ring-1 ring-inset',
        top ? 'bg-ink text-bg ring-ink' : 'bg-surface-2 text-ink ring-line',
        PLATE_SIZES[size],
        className,
      )}
      style={style}
      role="img"
      aria-label={label}
    >
      <span className="tabular">{level}</span>
      {notches && (
        <span className="mt-[10%] flex gap-[2px]" aria-hidden="true">
          {RANKS.map((rank) => (
            <span
              key={rank.level}
              className={cn(
                NOTCH_SIZES[size],
                rank.level <= level ? (top ? 'bg-bg' : 'bg-ink') : top ? 'bg-bg/30' : 'bg-line',
              )}
            />
          ))}
        </span>
      )}
    </span>
  );
}

/** Version compacta: el hoplon ocupa todo el lienzo, sin adornos. */
function PlainShield({ stroke }: { stroke: string }) {
  return (
    <svg viewBox="0 0 32 32" className="absolute inset-0 h-full w-full" aria-hidden="true">
      <path
        d={SHIELD_PATH_COMPACT}
        fill="rgb(var(--cp-surface-2))"
        stroke={stroke}
        strokeWidth="2"
      />
    </svg>
  );
}

const SHIELD_PATH_COMPACT =
  'M16 1.5 29 6.2v10.4c0 6-5.3 10.7-13 13.9-7.7-3.2-13-7.9-13-13.9V6.2z';

/** Hoplon del lienzo grande: mismo perfil, con sitio alrededor para adornos. */
const SHIELD_PATH =
  'M32 15 47 20.5v11.9C47 39.3 41 44.5 32 48.5 23 44.5 17 39.3 17 32.4V20.5z';

function OrnateShield({
  level,
  stroke,
  glow,
}: {
  level: number;
  stroke: string;
  glow: string;
}) {
  const studs = level >= 2;
  const spears = level >= 3;
  const laurel = level >= 4;
  const crest = level === 5;
  const crown = level >= 6;
  const king = level >= 7;

  return (
    <svg viewBox="0 0 64 64" className="absolute inset-0 h-full w-full" aria-hidden="true">
      {/* --- Rayos del Rey de Esparta, detras de todo --------------------- */}
      {king && (
        <g opacity="0.55">
          {Array.from({ length: 16 }, (_, i) => {
            const angle = (i / 16) * Math.PI * 2;
            const inner = 22;
            const outer = i % 2 === 0 ? 31 : 27;
            return (
              <line
                key={i}
                x1={32 + Math.cos(angle) * inner}
                y1={32 + Math.sin(angle) * inner}
                x2={32 + Math.cos(angle) * outer}
                y2={32 + Math.sin(angle) * outer}
                stroke={stroke}
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            );
          })}
        </g>
      )}

      {/* --- Lanzas cruzadas ---------------------------------------------- */}
      {spears && (
        <g stroke={stroke} strokeWidth="1.6" strokeLinecap="round" opacity="0.9">
          <line x1="14" y1="16" x2="50" y2="50" />
          <line x1="50" y1="16" x2="14" y2="50" />
          {/* Puntas de lanza arriba y regatones abajo */}
          <path d="M14 16 l0-5 l3 4z" fill={stroke} stroke="none" />
          <path d="M50 16 l0-5 l-3 4z" fill={stroke} stroke="none" />
        </g>
      )}

      {/* --- Ramas de laurel a ambos lados --------------------------------- */}
      {laurel && (
        <>
          <LaurelBranch stroke={stroke} />
          <g transform="translate(64,0) scale(-1,1)">
            <LaurelBranch stroke={stroke} />
          </g>
          {/* El lazo que cierra la corona de laurel llega con el Polemarca. */}
          {crown && (
            <path
              d="M27 54 q5 4 10 0 M29 55 l-3 4 M35 55 l3 4"
              fill="none"
              stroke={stroke}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          )}
        </>
      )}

      {/* --- Penacho del Locago -------------------------------------------- */}
      {crest && (
        <path
          d="M32 6 c6 1.5 9 5.5 8.5 9.5 h-17 C23 11.5 26 7.5 32 6 Z"
          fill={stroke}
          opacity="0.85"
        />
      )}

      {/* --- Corona del Polemarca en adelante ------------------------------ */}
      {crown && (
        <>
          <path
            d="M21 16 V7 l5.5 4.5 L32 4 l5.5 7.5 L43 7 v9 z"
            fill={stroke}
            opacity="0.92"
          />
          {king && (
            <g fill="rgb(var(--cp-crimson))">
              <circle cx="32" cy="10.5" r="1.7" />
              <circle cx="24" cy="13" r="1.2" />
              <circle cx="40" cy="13" r="1.2" />
            </g>
          )}
        </>
      )}

      {/* --- Brasa: solo el rango maximo late ------------------------------ */}
      {king && (
        <path
          d={SHIELD_PATH}
          fill="none"
          stroke={stroke}
          strokeWidth="4"
          opacity="0.5"
          className="animate-ember"
          style={{ filter: 'blur(2.5px)', color: glow }}
        />
      )}

      {/* --- El hoplon ------------------------------------------------------ */}
      <path d={SHIELD_PATH} fill="rgb(var(--cp-surface-2))" stroke={stroke} strokeWidth="2" />

      {/* Doble aro interior: privilegio del rango maximo. */}
      {king && (
        <path
          d="M32 19 43 23v9.1c0 5.2-4.5 9.2-11 12.3-6.5-3.1-11-7.1-11-12.3V23z"
          fill="none"
          stroke={stroke}
          strokeWidth="0.9"
          opacity="0.65"
        />
      )}

      {/* --- Remaches del canto --------------------------------------------- */}
      {studs && (
        <g fill={stroke} opacity="0.8">
          <circle cx="32" cy="18.4" r="1" />
          <circle cx="43.5" cy="22.6" r="1" />
          <circle cx="20.5" cy="22.6" r="1" />
          <circle cx="44" cy="33" r="1" />
          <circle cx="20" cy="33" r="1" />
          <circle cx="38" cy="42.5" r="1" />
          <circle cx="26" cy="42.5" r="1" />
        </g>
      )}
    </svg>
  );
}

/** Rama de laurel del lado izquierdo; el derecho es su espejo. */
function LaurelBranch({ stroke }: { stroke: string }) {
  return (
    <g stroke={stroke} fill={stroke} opacity="0.85">
      <path d="M22 53 C13 48, 9 39, 11 28" fill="none" strokeWidth="1.5" strokeLinecap="round" />
      {[
        'M11 28 c-4-2.5 -6.5 0.5 -5.5 3.5 3.2 1 5.5-0.8 5.5-3.5z',
        'M11.3 34.5 c-4-2.5 -6.5 0.5 -5.5 3.5 3.2 1 5.5-0.8 5.5-3.5z',
        'M13 41 c-4-2.2 -6.3 0.9 -5.1 3.8 3.1 0.8 5.2-1.1 5.1-3.8z',
        'M16.5 47 c-3.8-1.8 -5.8 1.4 -4.4 4.1 3-0.4 4.7-1.6 4.4-4.1z',
      ].map((d) => (
        <path key={d} d={d} stroke="none" />
      ))}
    </g>
  );
}
